import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { instance } from "api/apiBase";
import { InputSize, Select } from "shared/components";
import { Avatar } from "shared/components/Avatar";
import { cx } from "shared/utils";
import { StageOption } from "./types";

interface StageSelectProps {
  value?: number;
  rowId?: string;
  options: StageOption[];
  inputClassName?: string;
  // Callers whose lead data lives under a different query key (e.g. the inbox
  // crm lead-details query) can refetch it here after a successful stage change.
  onChanged?: () => void | Promise<unknown>;
}

export const StageSelect = ({
  value,
  rowId,
  options,
  inputClassName,
  onChanged
}: StageSelectProps) => {
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationKey: ["LEAD_CHANGE_STAGE"],
    mutationFn: ({
      lead_id,
      stage_id
    }: {
      lead_id: string;
      stage_id: number;
    }) =>
      instance.post("/portal/v1/leads/bulk_change_stage", {
        lead_ids: [Number(lead_id)],
        stage_id
      }),
    onSuccess: () => {
      // HACK TO BYPASS STALE CACHE ISSUE
      const refetchPromise = new Promise<void>((resolve) => {
        setTimeout(async () => {
          await Promise.all([
            queryClient.refetchQueries({ queryKey: ["LEAD_ACTIVITY_LOG"] }),
            queryClient.refetchQueries({ queryKey: ["LEADS_TABLE"] }),
            queryClient.refetchQueries({ queryKey: ["LEAD", rowId] }),
            onChanged?.()
          ]);
          resolve();
        }, 500);
      });

      toast.promise(
        refetchPromise,
        {
          pending: "Updating stage...",
          success: "Stage updated"
        },
        { position: "bottom-right", closeButton: true }
      );
    }
  });

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Select
        menuPortalTarget={document.body}
        menuPosition="fixed"
        className={cx("!rounded-lg max-w-[210px]", inputClassName)}
        size={InputSize.SM}
        id={`assigned-to-select-${rowId}`}
        value={value || "-"}
        options={options}
        onChange={(e) => {
          mutateAsync({
            lead_id: String(rowId),
            stage_id: Number(e.target.value)
          });
        }}
        legacyErrorMargins={false}
        placeholder="Unassigned"
        formatOptionLabel={(option) => {
          const stageOption = option as StageOption;
          return (
            <div className="flex items-center gap-2 py-1">
              <Avatar
                fallbackText=""
                fallbackBackgroundColor={stageOption.color}
              />
              <span>{stageOption.label}</span>
            </div>
          );
        }}
      />
    </div>
  );
};
